/**
 * First table column: RMA id with attention/gift/out-of-policy indicators, order link, and created date.
 */
import type { MouseEvent } from "react";
import { observer } from "mobx-react";
import DateTimeV2, { DATETIME_VIEW } from "../../../../../components/datetime-v2/datetime-v2";
import FontBody2 from "../../../../../components/fonts/font-body2/font-body2";
import { FONT_COLOR, FONT_VARIANT } from "../../../../../components/fonts/font-props";
import IconGift from "../../../../../components/icons/icon-gift/icon-gift";
import Icon from "../../../../../components/icons/icon/icon";
import IconSirenOnLight from "../../../../../components/icons/icon-siren-on-light/icon-siren-on-light";
import { Tooltip } from "../../../../../components/tooltip/tooltip";
import useDashboardTranslation from "../../../../../dashboard-translation/hooks/dashboard-translation-hook";
import { useDateTimeFormattingParams } from "../../../../../hooks/use-datetime-formatting-params";
import ExternalLink from "../../../components/external-link/external-link";
import RmaListItemStore from "../../../rma-list-page/stores/rma-item-store";
import RmaListRowRequireAttentionIcon from "./rma-list-row-require-attention-icon/rma-list-row-require-attention-icon";
import "./table-column-rma-details.scss";
import FontHeading5 from "../../../../../components/fonts/font-heading5/font-heading5";

export interface ITableColumnRmaDetailsProps {
    rma: RmaListItemStore;
    onOrderNumberClick: (e: MouseEvent<HTMLDivElement>) => void;
}

const TableColumnRmaDetails: React.FC<ITableColumnRmaDetailsProps> = observer(({ rma, onOrderNumberClick }) => {
    const scope = "dashboard-returns-page";
    const { getTranslation } = useDashboardTranslation();
    const dateTimeParams = useDateTimeFormattingParams();
    return (
        <div className="table-column-rma-details">
            <div className="table-column-rma-details__line table-column-rma-details__line--primary">
                <FontHeading5 variant={FONT_VARIANT.BOLD} color={FONT_COLOR.PRIMARY2}>
                    {getTranslation("RMA", scope)}
                    {rma.rmaId}
                </FontHeading5>
                {rma.isGift && (
                    <div className="table-column-rma-details__indicators">
                        <IconGift />
                    </div>
                )}
                {rma.isOutOfPolicy && (
                    <div className="table-column-rma-details__indicators">
                        <Tooltip
                            content={
                                <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>
                                    {getTranslation("RMA_OUT_OF_POLICY_INDICATOR_TEXT", scope)}
                                </FontBody2>
                            }
                        >
                            <Icon Component={IconSirenOnLight} />
                        </Tooltip>
                    </div>
                )}
                <div className="table-column-rma-details__indicators">
                    {rma.lastUnresolvedError && <RmaListRowRequireAttentionIcon errorText={rma.lastUnresolvedError} />}
                </div>
            </div>
            <div className="table-column-rma-details__line table-column-rma-details__line--secondary">
                <div className="table-column-rma-details__order" onClick={(e) => onOrderNumberClick(e)}>
                    <ExternalLink label={rma.orderName} url={rma.orderUrl} Font={FontBody2} />
                </div>
                <FontBody2 color={FONT_COLOR.PRIMARY3_50}>
                    <span className="table-column-rma-details__sep">|</span>
                    <DateTimeV2 {...dateTimeParams} utcDateString={rma.dateString} dateTimeView={DATETIME_VIEW.DATE_ONLY} />
                </FontBody2>
            </div>
        </div>
    );
});

export default TableColumnRmaDetails;
